import { CAMPAIGN_LINEAGE_QUERY } from "@lib/graphql/queries";
import { useMemo, useState } from "react";
import { useQuery } from "urql";
import { useCampaignDetail } from "./context";
import { CampaignLineageGraph } from "./lineage/CampaignLineageGraph";
import type {
  LineageEdge,
  LineageEdgeKind,
  LineageEdgeState,
  LineageGraph,
  LineageNodeKind,
  OpenArtefact,
} from "./lineage/types";
import { VersionHistoryPanel } from "./lineage/VersionHistoryPanel";

type QueryData = {
  campaign: {
    id: string;
    title: string;
    lineage: LineageGraph;
  } | null;
};

// graphql-ruby sends enum values in SCREAMING_SNAKE_CASE; the rest of the
// lineage code (nodeTypes map, kind comparisons, edge styling) is keyed in
// snake_case to match the underlying Ruby model identifiers.
function normalize(raw: LineageGraph): LineageGraph {
  const lower = (v: string) => v.toLowerCase();
  return {
    nodes: raw.nodes.map((n) => ({ ...n, kind: lower(n.kind) as LineageNodeKind })),
    edges: raw.edges.map((e: LineageEdge) => ({
      ...e,
      kind: lower(e.kind) as LineageEdgeKind,
      state: lower(e.state) as LineageEdgeState,
    })),
  };
}

export function LineageTab() {
  const { campaign } = useCampaignDetail();
  const [open, setOpen] = useState<OpenArtefact | null>(null);

  const [result] = useQuery<QueryData>({
    query: CAMPAIGN_LINEAGE_QUERY,
    variables: { id: campaign.id },
  });

  const lineage = useMemo(
    () => (result.data?.campaign?.lineage ? normalize(result.data.campaign.lineage) : undefined),
    [result.data?.campaign?.lineage],
  );

  return (
    <div className="space-y-4">
      <section>
        <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-text-muted">
          Lineage
        </h2>
        <p className="mb-3 text-sm text-text-secondary">
          Every versioned artefact on this campaign and how they depend on each other. Click any
          node to view its full version history.
        </p>
        {result.fetching && !lineage && (
          <p className="text-sm text-text-muted">Building the graph…</p>
        )}
        {result.error && (
          <p className="text-sm text-red-600">Could not load lineage: {result.error.message}</p>
        )}
        {lineage && (
          <CampaignLineageGraph
            lineage={lineage}
            selectedNodeId={open?.nodeId ?? null}
            onOpenHistory={setOpen}
          />
        )}
      </section>
      <VersionHistoryPanel campaignId={campaign.id} open={open} onClose={() => setOpen(null)} />
    </div>
  );
}
